import {
  Button,
  Badge,
  ButtonGroup,
  Checkbox,
  Command,
  CommandItem,
  CommandList,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import {
  STATUS,
  Status,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/types';
import { faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectAllItem } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/Filter/SelectAllItem';

// eslint-disable-next-line max-lines-per-function
export function StatusFilter() {
  const { t } = useTranslation();
  const { store } = useRotationsplanFeatureContext();
  const {
    ui: {
      uebersicht: {
        filter: { status: statusFilter },
      },
    },
  } = store;

  const [selectedStatus, setSelectedStatus] = useState<Status[]>(statusFilter);
  const allStatuses = STATUS.map(s => s)

  function onChangeSelectedStatus(status: Status) {
    if (selectedStatus.includes(status)) {
      const newStatus = selectedStatus.filter((s) => status !== s);
      setSelectedStatus(newStatus);
      store.setUebersichtStatusFilter(newStatus);
    } else {
      setSelectedStatus([...selectedStatus, status]);
      store.setUebersichtStatusFilter([...selectedStatus, status]);
    }
  }

  function deselectAll() {
    setSelectedStatus([]);
    store.setUebersichtStatusFilter([]);
  }

  function selectAll() {
    setSelectedStatus(allStatuses);
    store.setUebersichtStatusFilter(allStatuses);
  }

  return (
    <>
      <Popover>
        <PopoverTrigger className="hidden md:flex" asChild>
          <FilterTrigger
            selectedStatus={selectedStatus}
            deselectAll={deselectAll}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-full p-2 px-0 bg-white md:max-w-70">
          <Command>
            <CommandList>
              <div className="mx-2 flex items-center border-b border-neutrals-300 px-2 py-3.5 text-sm font-bold">
                <SelectAllItem
                  allCount={allStatuses.length}
                  selectedCount={selectedStatus.length}
                  selectAll={selectAll}
                  deselectAll={deselectAll}
                />
              </div>
              {STATUS.map((status) => (
                <CommandItem
                  key={status}
                  value={status}
                  onSelect={() => onChangeSelectedStatus(status)}>
                  <ListItem
                    status={status}
                    selectedStatus={selectedStatus}
                    onChangeSelectedStatus={onChangeSelectedStatus}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer>
        <DrawerTrigger className="flex md:hidden" asChild>
          <FilterTrigger
            selectedStatus={selectedStatus}
            deselectAll={deselectAll}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t('domain.mitarbeiter.rotationsplan.filter.status.label')}
          </div>
          <div
            className="flex flex-col gap-4">
            <SelectAllItem
              allCount={allStatuses.length}
              selectedCount={selectedStatus.length}
              selectAll={selectAll}
              deselectAll={deselectAll}
            />
            <div className="w-full h-px bg-neutrals-200"></div>
          </div>
          {STATUS.map((status, index) => (
            <div
              className="flex flex-col gap-4"
              key={status}
              onClick={() => onChangeSelectedStatus(status)}>
              <ListItem
                selectedStatus={selectedStatus}
                status={status}
                onChangeSelectedStatus={onChangeSelectedStatus}
              />
              {index < STATUS.length - 1 && (
                <div className="w-full h-px bg-neutrals-200"></div>
              )}
            </div>
          ))}
        </DrawerContent>
      </Drawer>
    </>
  );
}

function FilterTrigger({
  selectedStatus,
  deselectAll,
  ...props
}: {
  selectedStatus: Status[];
  deselectAll: () => void;
} & HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  return (
    <ButtonGroup {...props}>
      <Button className="flex items-center gap-2" variant="outline-secondary">
        {t('domain.mitarbeiter.rotationsplan.filter.status.label')}
        {selectedStatus.length > 0 && (
          <Badge type="pill" variant="secondary">
            {selectedStatus.length}
          </Badge>
        )}
      </Button>
      {selectedStatus.length > 0 && (
        <Button
          variant="outline-secondary"
          onClick={(e) => {
            e.preventDefault();
            deselectAll();
          }}>
          <FontAwesomeIcon icon={faXmarkLarge} />
        </Button>
      )}
    </ButtonGroup>
  );
}

function ListItem({
  selectedStatus,
  status,
  onChangeSelectedStatus,
}: {
  selectedStatus: Status[];
  status: Status;
  onChangeSelectedStatus: (status: Status) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between w-full gap-2 font-normal">
      <div className="text-sm truncate whitespace-nowrap">
        {t(`domain.mitarbeiter.rotationsplan.filter.status.${status}`)}
      </div>
      <Checkbox
        checked={selectedStatus.includes(status)}
        onCheckedChange={() => onChangeSelectedStatus(status)}
        className="disabled:opacity-100 disabled:cursor-auto"
      />
    </div>
  );
}
